import PhoneIcon from '/media/illustrations/34.svg'
import { Input } from '@/app/common/components/input/Input.tsx'
import { Button } from '@/app/common/components/button/Button.tsx'
import { SyntheticEvent, useEffect, useState } from 'react'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { useNavigate } from 'react-router-dom'
import { AppDispatch, RootState } from '@/storage/store.ts'
import { useDispatch, useSelector } from 'react-redux'
import {
  ResendTwoFA,
  VerifyTwoFA,
  // VerifyTwoFA
} from '@/apis/users/requests.ts'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import { fetchUserPrivileges, setVerified } from '@/app-store/user/authSlice.ts'
import { AuthLayout } from '../layout.tsx'
import { CircleLoader } from '@/common/components/CircleLoader.tsx'
import { ENV } from '@/common/constants.ts'
import { useUserPrivileges } from '../../../../hook/useUserPrivileges.tsx'

const defaultRoutes = [
  { permission: 'dashboard_read', path: AppRoutePaths.dashboard.index },
  {
    permission: 'applicants_read',
    path: AppRoutePaths.applicants.pre_selected,
  },
  { permission: 'appeals_read', path: AppRoutePaths.appeals.index },
  { permission: 'task_list_view_read', path: AppRoutePaths.tasks.index },
  {
    permission: 'scholarship_management_read',
    path: AppRoutePaths.scholarships.active_scholars.index,
  },
  { permission: 'programs_read', path: AppRoutePaths.setup.program.index },
  { permission: 'users_read', path: AppRoutePaths.setup.users.index },
  { permission: 'roles_read', path: AppRoutePaths.setup.roles.index },
  { permission: 'not_access', path: AppRoutePaths.not_access.index },
]

export const TwoFAuth = () => {
  const { fireAlert } = useAppAlert()
  const navigate = useNavigate()
  const [verificationCode, setVerificationCode] = useState(Array(6).fill(''))
  const [loadingResend, setLoadingResend] = useState(false)
  const [loadingVerify, setLoadingVerify] = useState(false)

  const asyncDispatch = useDispatch<AppDispatch>()
  const { phone, privilegesStatus, privileges } = useSelector(
    (state: RootState) => state.auth
  )

  const accessRoles = useUserPrivileges()

  const handleCodeChange = (value: string, index: number) => {
    if (value.length > 0 && isNaN(Number(value))) return
    const newCode = [...verificationCode]
    newCode[index] = value
    setVerificationCode(newCode)

    if (value && index < 5) {
      document.getElementById(`code-${index + 1}`)?.focus()
    }
    if (!value && index != 0) {
      document.getElementById(`code-${index - 1}`)?.focus()
    }
  }

  const handleNavigation = async (event: SyntheticEvent) => {
    event.preventDefault()

    const nonEmptyStrings = verificationCode.filter((str) => str.trim() !== '')
    if (nonEmptyStrings.length < 6) {
      fireAlert({
        variant: 'error',
        message: 'Please add a valid code',
      }).then()
      return
    }
    setLoadingVerify(true)

    //bypass
    if (
      ENV.VITE_API_URL.includes('.test') ||
      ENV.VITE_API_URL.includes('dev-api') ||
      ENV.VITE_API_URL.includes('sandbox-api') ||
      ENV.VITE_API_URL.includes('stage-api') ||
      ENV.VITE_API_URL.includes('demo-api')
    ) {
      await NavigateNext()
    } else {
      const { error } = await VerifyTwoFA(phone ?? '', nonEmptyStrings.join(''))
      setLoadingVerify(false)
      if (error) {
        await fireAlert({
          variant: 'error',
          message: error.message,
        })
        setVerificationCode(Array(6).fill(''))
      } else {
        asyncDispatch(setVerified())
        await NavigateNext()
      }
    }
  }

  useEffect(() => {
    if (privilegesStatus === 'succeeded') {
      const nextRoute = defaultRoutes.find(
        (route) =>
          accessRoles.user &&
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (accessRoles.user as any)[route.permission] === true
      ) ?? { permission: 'not_access', path: AppRoutePaths.not_access.index }
      sessionStorage.setItem('sidebarOpen', 'false')

      if (nextRoute.path && privileges.length > 0) {
        /*  navigate(path) */
      } else {
        navigate(AppRoutePaths.not_access.index)
      }
    }
  }, [privilegesStatus, privileges, defaultRoutes, accessRoles, navigate])

  // const NavigateNext = async () => {
  //   asyncDispatch(setVerified())
  //   asyncDispatch(fetchUserPrivileges())
  //   sessionStorage.setItem('sidebarOpen', "false");
  //   navigate(AppRoutePaths.programs.index)
  // }

  const NavigateNext = async () => {
    asyncDispatch(setVerified())
    await asyncDispatch(fetchUserPrivileges())
    navigate(AppRoutePaths.dashboard.index)
  }

  const handleResend = async () => {
    if (!phone) return
    setLoadingResend(true)
    const { error } = await ResendTwoFA(phone)
    setLoadingResend(false)
    if (error) {
      await fireAlert({
        variant: 'error',
        message: error.message,
      })
    } else {
      await fireAlert({
        variant: 'success',
        message: 'Verification code sent!',
      })
      setVerificationCode(Array(6).fill(''))
    }
  }

  return (
    <AuthLayout>
      <AuthLayout.Content>
        <form>
          <div className="flex flex-col gap-5 items-center">
            <img alt="Phone" src={PhoneIcon} className="mx-auto h-20 mb-2" />
            <div className="text-center mb-2">
              <h3
                data-testid="verify-phone-text"
                className=" text-lg font-semibold text-gray-900 mb-5"
              >
                Verify your phone
              </h3>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-500 mb-1.5">
                  Enter the verification code we sent to
                </span>
                <a className="text-sm font-semibold text-gray-900">
                  ******{phone?.slice(-4)}{' '}
                </a>
              </div>
            </div>
            <div className="flex space-x-2">
              {verificationCode.map((num, index) => (
                <Input
                  key={index}
                  id={`code-${index}`}
                  type="text"
                  maxLength={1}
                  value={num}
                  autoComplete="off"
                  onChange={(e) => handleCodeChange(e.target.value, index)}
                  className="w-10 h-10 text-center text-2xl border border-gray-300 bg-gray-50 rounded-md"
                />
              ))}
            </div>
            <div className="flex items-center justify-center gap-1 mb-2">
              <span className="text-xs font-medium text-gray-500">
                Didn’t get the code?
              </span>
              <a
                className="text-xs font-medium link text-primary"
                onClick={handleResend}
              >
                {loadingResend ? (
                  <CircleLoader size="sm" className="mr-1" />
                ) : (
                  'Resend'
                )}
              </a>
            </div>
            <Button className="w-full" onClick={handleNavigation}>
              <span>{loadingVerify ? 'Authenticating...' : 'Continue'}</span>
            </Button>
          </div>
        </form>
      </AuthLayout.Content>
    </AuthLayout>
  )
}
